import React, { useEffect, useState } from 'react';
import { adminStats, adminListContacts, adminListDonations } from '../../lib/api';
import { MessageSquare, Heart, FileText, Mail, Loader2, TrendingUp } from 'lucide-react';
import { Link } from 'react-router-dom';

export default function AdminDashboard() {
  const [stats, setStats] = useState(null);
  const [recentContacts, setRecent] = useState([]);
  const [recentDonations, setRecentDonations] = useState([]);

  useEffect(() => {
    adminStats().then(setStats);
    adminListContacts().then((d) => setRecent(d.slice(0, 5)));
    adminListDonations().then((d) => setRecentDonations(d.slice(0, 5)));
  }, []);

  if (!stats) {
    return <div className="p-10"><Loader2 className="w-6 h-6 animate-spin text-sky-500" /></div>;
  }

  const cards = [
    { icon: MessageSquare, label: 'Messages reçus', value: stats.contacts.total, sub: `${stats.contacts.unread} non lus`, color: 'from-sky-400 to-sky-600', link: '/admin/contacts' },
    { icon: Heart, label: 'Dons enregistrés', value: stats.donations.total, sub: `${(stats.donations.sum || 0).toLocaleString('fr-FR')} € total`, color: 'from-emerald-400 to-teal-500', link: '/admin/donations' },
    { icon: FileText, label: 'Articles', value: stats.articles.total, sub: `${stats.articles.published} publiés`, color: 'from-amber-400 to-orange-500', link: '/admin/articles' },
    { icon: Mail, label: 'Newsletter', value: stats.newsletter.total, sub: 'abonnés', color: 'from-purple-400 to-pink-500' },
  ];

  return (
    <div className="p-8 lg:p-10">
      <header className="mb-8">
        <h1 className="font-display font-extrabold text-3xl text-sky-950">Tableau de bord</h1>
        <p className="text-slate-500 mt-1">Vue d'ensemble de votre activité.</p>
      </header>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 mb-10">
        {cards.map((c, i) => {
          const Inner = (
            <div className="bg-white rounded-2xl border border-sky-100 p-6 hover:shadow-md hover:-translate-y-0.5 transition-all h-full">
              <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${c.color} flex items-center justify-center mb-4`}>
                <c.icon className="w-6 h-6 text-white" />
              </div>
              <div className="font-display font-extrabold text-3xl text-sky-950">{c.value}</div>
              <p className="text-sm text-slate-600 mt-1">{c.label}</p>
              <p className="text-xs text-slate-400 mt-1">{c.sub}</p>
            </div>
          );
          return c.link ? <Link key={i} to={c.link}>{Inner}</Link> : <div key={i}>{Inner}</div>;
        })}
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        <div className="bg-white rounded-2xl border border-sky-100 p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-display font-bold text-lg text-sky-950">Derniers messages</h3>
            <Link to="/admin/contacts" className="text-xs text-sky-700 font-semibold hover:underline">Tout voir</Link>
          </div>
          {recentContacts.length === 0 ? (
            <p className="text-sm text-slate-500 py-6 text-center">Aucun message pour l'instant.</p>
          ) : (
            <ul className="divide-y divide-sky-50">
              {recentContacts.map((c) => (
                <li key={c.id} className="py-3 flex items-start gap-3">
                  <div className="w-9 h-9 rounded-full bg-gradient-to-br from-sky-100 to-emerald-100 flex items-center justify-center text-xs font-bold text-sky-700 flex-shrink-0">
                    {c.name.slice(0, 2).toUpperCase()}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold text-sky-950 truncate">{c.name}</p>
                    <p className="text-xs text-slate-500 truncate">{c.message}</p>
                  </div>
                  {!c.read && <span className="w-2 h-2 rounded-full bg-emerald-500"></span>}
                </li>
              ))}
            </ul>
          )}
        </div>

        <div className="bg-white rounded-2xl border border-sky-100 p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-display font-bold text-lg text-sky-950">Derniers dons</h3>
            <Link to="/admin/donations" className="text-xs text-sky-700 font-semibold hover:underline">Tout voir</Link>
          </div>
          {recentDonations.length === 0 ? (
            <p className="text-sm text-slate-500 py-6 text-center">Aucun don pour l'instant.</p>
          ) : (
            <ul className="divide-y divide-sky-50">
              {recentDonations.map((d) => (
                <li key={d.id} className="py-3 flex items-center gap-3">
                  <div className="w-9 h-9 rounded-full bg-gradient-to-br from-emerald-100 to-sky-100 flex items-center justify-center flex-shrink-0">
                    <Heart className="w-4 h-4 text-emerald-700" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold text-sky-950 truncate">{d.name}</p>
                    <p className="text-xs text-slate-500">{d.frequency === 'monthly' ? 'mensuel' : 'unique'}</p>
                  </div>
                  <span className="font-display font-bold text-sky-950">{d.amount} €</span>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
}
